import DOMPurify from 'dompurify'
import { camelCase, mapKeys } from 'lodash'
import { marked } from 'marked'
import moment from 'moment'

import { Role } from './fixtures'
import restrictions, { RestrictionKey, VisibilityKey } from './restrictions'

export const convertKeysToCamelCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(convertKeysToCamelCase)

  return mapKeys(obj, (_, key) => camelCase(key))
}

export const ASSIGNED_PROCESSING_TYPE_TYPE = 'Beauftragte Bearbeitungsart'
export const INTENDED_PROCESSING_TYPE_TYPE = 'Angedachte Bearbeitungsart'
export const ACTUAL_PROCESSING_TYPE_TYPE = 'Tatsächliche Bearbeitungsart'

export const TOKEN_KEY = 'm2.access-token'
export const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token)
export const getToken = () => localStorage.getItem(TOKEN_KEY)
export const removeToken = () => localStorage.removeItem(TOKEN_KEY)

export const UID_KEY = 'm2.uid'
export const setUid = (uid: string) => localStorage.setItem(UID_KEY, uid)
export const getUid = () => localStorage.getItem(UID_KEY)
export const removeUid = () => localStorage.removeItem(UID_KEY)

export const CLIENT_KEY = 'm2.client'
export const setClient = (client: string) => localStorage.setItem(CLIENT_KEY, client)
export const getClient = () => localStorage.getItem(CLIENT_KEY)
export const removeClient = () => localStorage.removeItem(CLIENT_KEY)

export const EXPIRY_KEY = 'm2.expiry'
export const setExpiry = (expiry: string) => localStorage.setItem(EXPIRY_KEY, expiry)
export const getExpiry = () => localStorage.getItem(EXPIRY_KEY)
export const removeExpiry = () => localStorage.removeItem(EXPIRY_KEY)

export const getHeaders = () => ({
  'Content-type': 'application/json',
  'access-token': getToken() || '',
  'token-type': 'Bearer',
  uid: getUid() || '',
  client: getClient() || '',
  expiry: getExpiry() || ''
})

export const USER_ID_KEY = 'm2.user.id'
export const getUserId = () => localStorage.getItem(USER_ID_KEY)
export const setUserId = (id: string) => localStorage.setItem(USER_ID_KEY, id)
export const removeUserId = () => localStorage.removeItem(USER_ID_KEY)

export const USER_KEY = 'm2.user'
export const setUser = (user: any) => localStorage.setItem(USER_KEY, JSON.stringify(user))
export const getUser = () => localStorage.getItem(USER_KEY)
export const removeUser = () => localStorage.removeItem(USER_KEY)

export const SIDEBAR_KEY = 'm2.sidebar'
export const setSidebarOpen = (open: boolean) =>
  localStorage.setItem(SIDEBAR_KEY, JSON.stringify(open))

// NOTE: null throws no error, empty string does
// @ts-ignore
export const getSidebarOpen = () => JSON.parse(localStorage.getItem(SIDEBAR_KEY))

export const CALLBACK_CASES_FILTER_KEY = 'm2.callbacks.filter'
export const CALLBACK_CASES_SORT_KEY = 'm2.callbacks.sort'
export const CALLBACK_CASES_TEXTSEARCH_KEY = 'm2.cases.textSearch'
export const CALLBACK_CASES_LIMIT_KEY = 'm2.cases.limit'

// NOTE: null throws no error, empty string does
export const getStoredCasesColumns = (columnKey: string) =>
  JSON.parse(localStorage.getItem(columnKey) as string)

export const TIME_KEY = 'm2.logout.time'
export const setTimer = (time: string) => localStorage.setItem(TIME_KEY, time)
export const getTimer = () => localStorage.getItem(TIME_KEY)
export const removeTimer = () => localStorage.removeItem(TIME_KEY)

export const CASES_FILTER_KEY = 'm2.cases.filter'
export const POOLS_FILTER_KEY = 'm2.pools.filter'
export const POOLS_COLUMNS_KEY = 'm2.pools.columns'
export const CASES_SORT_KEY = 'm2.cases.sort'
export const CASES_SORT_PREVIOUS_KEY = 'm2.cases.sort.previous'
export const CASES_COLUMNS_KEY = 'm2.cases.columns'
export const CASES_COLUMNS_PREVIOUS_KEY = 'm2.cases.columns.previous'
export const CASES_TEXTSEARCH_KEY = 'm2.cases.textSearch'
export const CASES_LIMIT_KEY = 'm2.cases.limit'

const storeCasesColumns = (columns: any[], columnKey: string) =>
  localStorage.setItem(columnKey, JSON.stringify(columns))

export const setCasesColumns = (columns: any[], columnKey: string) => {
  const colsToStore = columns.map((col, index) => ({
    key: col.key,
    position: index + 1,
    fixed: col.fixed || false
  }))
  storeCasesColumns(colsToStore, columnKey)
}

export const COLUMN_SELECTION_TITLE_KEY = 'm2.cases.selection.title'
export const POOL_COLUMN_SELECTION_TITLE_KEY = 'm2.pools.selection.title'
export const getCurrentSelectionTitleFromStorage = (selectionKey: string) =>
  localStorage.getItem(selectionKey)
export const setCurrentSelectionTitleToStorage = (
  selectionKey: string,
  sectionTitle?: string | null
) =>
  sectionTitle
    ? localStorage.setItem(selectionKey, sectionTitle)
    : localStorage.removeItem(selectionKey)

export const isLogin = () => !!getToken()

export const getDateFromString = (date: string | Date | undefined | null) => {
  if (typeof date === 'object' && date instanceof Date && !isNaN(date.getTime())) {
    return date
  }
  if (!date || !moment(date).isValid()) {
    return null
  }

  return moment(date).toDate()
}

export const moveElementInArray = (arr: any[], from: number, to: number) => {
  arr.splice(to, 0, arr.splice(from, 1)[0])
  return arr
}

export const clearUserData = () => {
  removeToken()
  removeUid()
  removeClient()
  removeExpiry()
  removeUserId()
  removeUser()
  localStorage.removeItem(CASES_FILTER_KEY)
}

export interface GetNumberFromString {
  (value: string | number | undefined | null): number | null
}

export const getNumberFromString: GetNumberFromString = value => {
  if (value === null || value === undefined) return null
  const num = parseFloat(String(value))
  return Number.isNaN(num) ? null : num
}

export const getIntFromString = (value: string) => {
  // NOTE: ruby's max int value is 2147483647
  const MAX_INT_VALUE = 2147483647
  const number = !Number.isNaN(parseInt(value)) ? parseInt(value) : null
  if (number && number > MAX_INT_VALUE) return MAX_INT_VALUE
  return number
}

export const getNewFieldValue = (
  dirty: any,
  values: any,
  name: string,
  index?: number,
  arrayName?: string
) => {
  if (arrayName && index !== undefined && Number.isInteger(index)) {
    return dirty?.[arrayName]?.[index]?.[name]
      ? getNumberFromString(values?.[arrayName]?.[index]?.[name])
      : undefined
  }
  return dirty[name] ? getNumberFromString(values?.[name]) : undefined
}
export const getNewStringFieldValue = (
  dirty: any,
  values: any,
  name: string,
  index?: number,
  arrayName?: string
) => {
  if (arrayName && index !== undefined && Number.isInteger(index)) {
    const arrayDirty = dirty?.[arrayName]?.[index]?.[name]
    const arrayValue = values?.[arrayName]?.[index]?.[name]?.trim() || null

    return arrayDirty ? arrayValue : undefined
  }
  const dirtyValue = dirty[name]
  const value = values?.[name]?.trim() || null
  return dirtyValue ? value : undefined
}
export const getInsuranceDependentFieldValueFromArray = (
  dirty: any,
  values: any,
  name: string,
  index: number,
  arrayName: string
) => {
  if (dirty.insuranceId && dirty?.[arrayName]?.[index]?.[name]) {
    return values?.[arrayName]?.[index]?.[name]
  }
  if (dirty.insuranceId && !dirty?.[arrayName]?.[index]?.[name]) {
    return null
  }
  return values?.[arrayName]?.[index]?.[name]
}

export const getInsuranceDependentFieldValue = (dirty: any, values: any, name: string) => {
  if (dirty.insuranceId && dirty?.[name]) {
    return values?.[name]
  }

  if (dirty.insuranceId && !dirty?.name) {
    return null
  }
  return values?.[name]
}

export const getProjectDependentFieldValue = (dirty: any, values: any, name: string) => {
  if (dirty.projectId && dirty?.[name]) {
    return values?.[name]
  }

  if (dirty.projectId && !dirty?.name) {
    return null
  }
  return values?.[name]
}

export const getContentWithVariables = (
  content: string,
  variables: Record<string, string | number | boolean | null | undefined>
) => {
  return content?.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
    const value = key.split('.').reduce((acc: any, k: any) => acc?.[k], variables)

    if (value === undefined || value === null) return ''
    return String(value)
  })
}

export const getProjectDependentFieldValueFromArray = (
  dirty: any,
  values: any,
  name: string,
  index: number,
  arrayName: string
) => {
  if (dirty.projectId && dirty?.[arrayName]?.[index]?.[name]) {
    return values?.[arrayName]?.[index]?.[name]
  }
  if (dirty.projectId && !dirty?.[arrayName]?.[index]?.[name]) {
    return null
  }
  return values?.[arrayName]?.[index]?.[name]
}

export const getProcessingsAttributesValue = (processingsAttributes: any, dirty: any, vals: any) =>
  processingsAttributes.map((pa: any, i: number) => {
    // Filter out temporary _closingResult fields used for hierarchical selection
    const filteredPa = Object.fromEntries(
      Object.entries(pa).filter(([key]) => !key.startsWith('_closingResult'))
    )
    return {
      ...filteredPa,
      payments: undefined,
      totalPayments: undefined,
      paymentRequestCost: undefined,
      paymentRequestCostSource: undefined,
      feedbacks: undefined,
      totalRefunds: undefined,
      incomeWithTax: undefined,
      refunded: undefined,
      refundAmount: undefined,
      refundRequired: undefined,
      finalReportUrl: undefined,
      finalReportSentAt: undefined,
      deletable: undefined,
      agent: undefined,
      assignedFrom: undefined,
      involvedUsers: undefined,
      m2Case: undefined,
      lastActionAt: undefined,
      nextTaskAt: undefined,
      nextTaskComment: undefined,
      firstCallDelayed: undefined,
      costFromActualProcessingType: undefined,
      closingResult: undefined, // Result object - only closingResultId should be sent
      settlementType: undefined, // SettlementType object - only settlementTypeId should be sent
      closingType: undefined, // ClosingType object - only closingTypeId should be sent
      agentName: undefined, // Display-only field for showOnlyField, not part of GraphQL input
      agentFitDisplay: undefined, // Display-only translated agentFit, not part of GraphQL input
      caseEmailAddress: undefined, // Read-only Plus-Adresse aus der Query, not part of GraphQL input
      cost: getNewFieldValue(dirty, vals, 'cost', i, 'processingsAttributes'),
      additionalCost: getNewFieldValue(dirty, vals, 'additionalCost', i, 'processingsAttributes'),
      income: getNewFieldValue(dirty, vals, 'income', i, 'processingsAttributes'),
      additionalIncome: vals.processingsAttributes[i]?.additionalIncome
        ? getNewFieldValue(dirty, vals, 'additionalIncome', i, 'processingsAttributes')
        : 0,
      receivedPayment: getNewFieldValue(dirty, vals, 'receivedPayment', i, 'processingsAttributes'),
      intendedProcessingType: undefined,
      intendedProcessingTypeId: getProjectDependentFieldValueFromArray(
        dirty,
        vals,
        'intendedProcessingTypeId',
        i,
        'processingsAttributes'
      ),
      invoice: undefined,
      actualProcessingType: undefined,
      actualProcessingTypeId: getProjectDependentFieldValueFromArray(
        dirty,
        vals,
        'actualProcessingTypeId',
        i,
        'processingsAttributes'
      ),
      assignedProcessingType: undefined,
      assignedProcessingTypeId: getProjectDependentFieldValueFromArray(
        dirty,
        vals,
        'assignedProcessingTypeId',
        i,
        'processingsAttributes'
      ),
      settlementTypeId: getProjectDependentFieldValueFromArray(
        dirty,
        vals,
        'settlementTypeId',
        i,
        'processingsAttributes'
      ),
      coverage: getNewStringFieldValue(dirty, vals, 'coverage', i, 'processingsAttributes'),
      coverageComments: getNewStringFieldValue(
        dirty,
        vals,
        'coverageComments',
        i,
        'processingsAttributes'
      ),
      paymentProtocols: undefined,
      customerInvoice: undefined,
      payoutStatus: undefined,
      paymentStatus: undefined,
      payoutAddedAt: undefined,
      createdAt: undefined
    }
  })

export const getDisabledFields = (fields: any, userRole: Role, setName: RestrictionKey) => {
  const disable = restrictions()
    ?.[setName]?.filter(item => item.roles.includes(userRole))
    ?.map(item => item.name)

  return fields?.map((field: any) =>
    disable.includes(field.permissionId) ? { ...field, disabled: true } : { ...field }
  )
}
export const getDisabledMenuItems = (role: Role) =>
  restrictions()
    .menuItems.filter(item => item.roles?.includes(role))
    ?.map(item => item.name)

export const getDefaultEntryPoint = () => restrictions().defaultEntry

export const getUserEntryPoint = (role: Role) =>
  restrictions()
    .entries.filter(item => item.roles?.includes(role))
    ?.map(item => item.name)[0] || getDefaultEntryPoint()

export const getDisabledRoutes = (role: Role) =>
  restrictions()
    .routes.filter(route => route.roles?.includes(role))
    ?.map(route => route.name)

export const getDisabledElement = (role: Role, level: VisibilityKey) => {
  const found = restrictions()?.[level]?.find(item => item?.role === role)?.visibility
  return { hidden: found === 'hidden', disabled: found === 'disabled' }
}

export const resolveNestedObject = (path: string, obj: any) =>
  path?.split('.')?.reduce((prev, curr) => (prev !== undefined ? prev?.[curr] : null), obj)

export const getAddress = (customer: any) => {
  // Helper function to check if a value is valid (not null, undefined, empty string, or "null")
  const isValidValue = (value: any) =>
    value !== null && value !== undefined && value !== '' && value !== 'null' && value !== 'nil'

  // Build street address from valid street and house number values
  const streetParts = [customer?.street, customer?.houseNumber].filter(isValidValue)
  const street = streetParts.length > 0 ? streetParts.join(' ') : undefined

  // Build postal address from valid postal code and place values
  const postalParts = [customer?.postalCode, customer?.place].filter(isValidValue)
  const postal = postalParts.length > 0 ? postalParts.join(' ') : undefined

  // Combine street and postal if available, otherwise return dash or empty string
  const addressParts = [street, postal].filter(part => part !== undefined)
  return addressParts.length > 0 ? addressParts.join(', ') : ''
}

export const arrayUniqueByKey: (array: any[], key: string) => any[] = (array, key) => {
  const map = new Map(array.map(item => [item[key], item]))
  return Array.from(map.values())
}

export const getEmptyContactPerson = () => ({
  salutation: undefined,
  firstname: undefined,
  lastname: undefined,
  email: undefined,
  relationToPolicyholder: undefined,
  relationToConflictPartner: undefined,
  phoneNumber: undefined,
  mobileNumber: undefined
})

export const getEmptyCustomer = () => ({
  policyNumber: undefined,
  salutation: undefined,
  lastname: undefined,
  firstname: undefined,
  birthDate: undefined,
  company: undefined,
  street: undefined,
  houseNumber: undefined,
  postalCode: undefined,
  place: undefined,
  country: undefined,
  phoneNumber: undefined,
  mobileNumber: undefined,
  email: undefined,
  comments: undefined,
  firstSignInAt: undefined
})

export const createRegExpWithFlags = (pattern: string) => {
  // Regular expression to match flag patterns like (?i), (?g), etc.
  const flagPattern = /\(\?[a-z]\)/g

  // Extract flags from the pattern
  let flags = ''
  let match = flagPattern.exec(pattern)
  while (match !== null) {
    // Extract the flag character and append it to the flags string
    flags += match[0][2]
    match = flagPattern.exec(pattern)
  }

  // Remove the flag patterns from the original pattern
  const cleanedPattern = pattern.replace(flagPattern, '')

  // Create the RegExp object with the cleaned pattern and extracted flags
  const regex = new RegExp(cleanedPattern, flags)

  return regex
}

export const getSalutation = (salutation: string, lastName: string): string => {
  if (!salutation || !lastName) {
    return ''
  }

  return ` ${salutation} ${lastName}`
}

export const getCustomerInvoiceData = (m2Case: any, customerInvoice: any): any => {
  if (customerInvoice) {
    return {
      sentAt: getDateFromString(customerInvoice.sentAt),
      documentUrl: customerInvoice.documentUrl,
      // Cutover (docs/35 K3): ohne die Felder greifen Badge und
      // Doppel-Push-Schutz der Mandanten-Rechnung nie.
      financeStatus: customerInvoice.financeStatus,
      financeStatusUpdatedAt: customerInvoice.financeStatusUpdatedAt,
      financeInvoiceUrl: customerInvoice.financeInvoiceUrl,
      invoiceNo: customerInvoice.invoiceNo,
      agent: customerInvoice.agent,
      dataset: {
        ...customerInvoice.dataset,
        customer: {
          ...customerInvoice.dataset.customer,
          houseNumber: customerInvoice.dataset.customer?.house_number || '',
          postalCode: customerInvoice.dataset.customer?.postal_code || '',
          birthDate: getDateFromString(customerInvoice.dataset.customer?.birth_date) || ''
        }
      }
    }
  }
  return {
    dataset: {
      customer: {
        ...m2Case.policyholder
      }
    }
  }
}

export const capitalizeFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1)

export const setDocumentTitle = (
  m2Case: any,
  prefix: string = 'm2',
  fallback: string = 'Fall bearbeiten'
) => {
  const { policyholder } = m2Case || {}
  const title =
    policyholder?.company ||
    policyholder?.lastname ||
    ((m2Case?.claimNumber?.length || 0) > 1 ? m2Case.claimNumber : policyholder?.policyNumber)
  document.title = `${prefix || import.meta.env.VITE_TITLE} | ${title || fallback}`
}

export const resetDocumentTitle = (prefix: string = 'm2') => {
  document.title = prefix || import.meta.env.VITE_TITLE || 'm2'
}

export const getInitialDates = () => {
  const today = new Date()
  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = today.getDay()
  // Calculate how many days to subtract to get to last Monday
  const daysSinceMonday = (dayOfWeek + 6) % 7

  // Last week's Monday
  const lastWeekStart = new Date(today)
  lastWeekStart.setDate(today.getDate() - daysSinceMonday - 7)

  // Last week's Sunday
  const lastWeekEnd = new Date(lastWeekStart)
  lastWeekEnd.setDate(lastWeekStart.getDate() + 6)

  // Week before last's Monday
  const weekBeforeStart = new Date(lastWeekStart)
  weekBeforeStart.setDate(lastWeekStart.getDate() - 7)

  // Week before last's Sunday
  const weekBeforeEnd = new Date(weekBeforeStart)
  weekBeforeEnd.setDate(weekBeforeStart.getDate() + 6)

  return {
    lastWeek: {
      start: lastWeekStart.toISOString().slice(0, 10),
      end: lastWeekEnd.toISOString().slice(0, 10)
    },
    weekBefore: {
      start: weekBeforeStart.toISOString().slice(0, 10),
      end: weekBeforeEnd.toISOString().slice(0, 10)
    }
  }
}

export const transformMarkdownToHtml = async (markdown: string) => {
  const htmlContent = await marked(markdown || '')
  return DOMPurify.sanitize(htmlContent)
}

// A masked input keeps the maskChar ('_') for unfilled positions, so a value
// counts as complete only once no placeholder is left.
export const isPolicyOrClaimNumberComplete = (
  value?: string | null,
  mask?: string | null,
  maskChar = '_'
): boolean => {
  if (!value) return false
  if (mask && value.includes(maskChar)) return false
  return true
}
